import { createContext, useContext } from 'react';
import type { AppCode } from '../constants';

export const GLOBAL_SETTINGS_STORAGE_KEY = 'uama:global-settings';

export type ServiceNetwork = 'facebook' | 'tiktok';

export interface GlobalSettings {
  app?: AppCode;
  network?: ServiceNetwork;
}

export interface GlobalSettingsContextValue {
  selectedApp?: AppCode;
  setSelectedApp: (app: AppCode) => void;
  selectedNetwork?: ServiceNetwork;
  setSelectedNetwork: (network: ServiceNetwork) => void;
}

export const readGlobalSettings = (): GlobalSettings => {
  try {
    const raw = window.localStorage.getItem(GLOBAL_SETTINGS_STORAGE_KEY);
    if (!raw) return {};

    return JSON.parse(raw) as GlobalSettings;
  } catch {
    // Storage may be unavailable or corrupted - fall back to empty settings.
    return {};
  }
};

export const writeGlobalSettings = (settings: GlobalSettings): void => {
  try {
    window.localStorage.setItem(GLOBAL_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Ignore write errors (e.g. storage disabled in private mode).
  }
};

const DEFAULT_CONTEXT: GlobalSettingsContextValue = {
  selectedApp: undefined,
  setSelectedApp: () => undefined,
  selectedNetwork: undefined,
  setSelectedNetwork: () => undefined,
};

export const GlobalSettingsContext =
  createContext<GlobalSettingsContextValue>(DEFAULT_CONTEXT);

export const useGlobalSettings = (): GlobalSettingsContextValue =>
  useContext(GlobalSettingsContext);
